import { CommonModule } from '@angular/common';
import { Component, effect, inject, Input, OnDestroy, OnInit, signal } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { VatRateDetailsFormService } from './vat-rate-details-form.service';
import { ToolbarEventsContextService } from 'src/app/core/services/toolbar-events/toolbar-events-context.service';

import {
  ConfigurationManagerService,
  ConnectionService,
  EntityManagerService,
  LoaderService,
  ToolbarEventsService,
  UserActiveSessionManagerService
} from 'src/app/core';

import { AlertDialogService, ConfirmDialogService, GenericJsonDetailDialogService } from 'src/app/dialogs';
import { MatSnackBar } from '@angular/material/snack-bar';
import { $state } from 'app/ajs-upgraded-providers';
import { Vat } from 'tilby-models';
import { startWith, Subject, takeUntil } from 'rxjs';
import { VatRatesForm, VatRatesFormGroups } from '../vat-rates-model';
import { EncodedData, vatCodeList } from 'src/app/features/items/utils/item.constants';
import { MatSelectModule } from '@angular/material/select';
import { ItemDataCatalogService } from 'src/app/features/items/services/item-data-catalog.service';

@Component({
  selector: 'app-vat-rate-details',
  standalone: true,
  imports: [ MatFormFieldModule, ReactiveFormsModule,
    TranslateModule, FormsModule, CommonModule,
    MatInputModule, MatSelectModule
  ],
  templateUrl: './vat-rate-details.component.html',
  styleUrl: './vat-rate-details.component.scss'
})
export class VatRateDetailsComponent implements OnInit, OnDestroy {

  readonly vatRateFormService = inject(VatRateDetailsFormService);
  private readonly _snackBar = inject(MatSnackBar);
  private readonly alertDialogService = inject(AlertDialogService);
  private readonly configurationManagerService = inject(ConfigurationManagerService);
  private readonly confirmDialogService = inject(ConfirmDialogService);
  private readonly connection = inject(ConnectionService);
  private readonly entityManager = inject(EntityManagerService);
  private readonly genericJsonDetailDialog = inject(GenericJsonDetailDialogService);
  private readonly itemDataCatalogService = inject(ItemDataCatalogService);
  private readonly loaderService = inject(LoaderService);
  private readonly state = inject($state);
  private readonly toolbarEventsContextService = inject(ToolbarEventsContextService);
  private readonly toolbarEventsService = inject(ToolbarEventsService);
  private readonly translate = inject(TranslateService);
  private readonly userActiveSessionManagerService = inject(UserActiveSessionManagerService);
  
  protected readonly hasToolbarMenu = this.configurationManagerService.isUserPermittedOptIn("show_developer_options");

  private areEditsBlocked = this.configurationManagerService.isMagoEnabled() && this.userActiveSessionManagerService.getSession()?.role !== 'root';

  @Input() vatRate!: Vat;
  vats: Vat[] =[];
  
  private destroy$ = new Subject<void>(); 

  isNew: boolean = false;
  vatRateForm!: VatRatesFormGroups;
  vatCodeList: Array<EncodedData> = [];
  country: string = "";

  isDataListReady = this.itemDataCatalogService.isReady;
  vatRateFormReady = this.vatRateFormService.vatRateFormReady;
  
  constructor() {
    effect(() => {
      if(this.isDataListReady()) {
        this.readData();
        this.vats = this.itemDataCatalogService.getDataByKey("vat") || [];
        this.vatRateForm = this.vatRateFormService.createForm(this.vatRate);
        this.loaderService.forceHide();
        this.initContextToolbarLabel();
      }
    }, { allowSignalWrites: true });

    effect(() => {
      if(this.vatRateFormReady()) {
        this.listenToVatRateNameChanges();
        this.listenToValueChanges();
      }
    })
  }

  ngOnInit(): void {
    this.isNew = !this.vatRate || !(this.vatRate.id >= 0);
    this.createToolbarButtons();
  }

  readData() {
    this.vatCodeList = [...vatCodeList];
    this.country = this.configurationManagerService.getShopCountry();
  }

  initContextToolbarLabel() {
    const currentData = this.vatRateFormService.form.getRawValue();
    this.toolbarEventsContextService.label = this.buildVatLabel(currentData);
  }
  
  private buildVatLabel(vatRate: VatRatesForm): string {
    if (this.isNew && !vatRate.value && !vatRate.code) {
      return this.translate.instant('ITEMS.VAT_RATE.DETAILS.NAME_PLACEHOLDER');
    }
    const id = vatRate?.id?.toString() ?? "";
    const name = this.translate.instant('ITEMS.VAT_RATE.VAT_NAME', { id: id });
    
    let valueSuffix = vatRate.value ? `${vatRate.value}%`: '';
    
    if (vatRate.code) {
      valueSuffix = `${vatRate.value}% (${vatRate.code})`;
    }

    return valueSuffix ? `${name} - ${valueSuffix}`: `${name}`;
  }
  
  private listenToVatRateNameChanges() {
    this.vatRateFormService.form?.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((formData: any) => {
      this.toolbarEventsContextService.label = this.buildVatLabel(formData);
    })
  }

  private listenToValueChanges() {
    this.vatRateFormService.value?.valueChanges
    .pipe(
      startWith(this.vatRateFormService.value?.value),
      takeUntil(this.destroy$) 
    )
    .subscribe((val: number) => {
      const codeControl = this.vatRateFormService.code;
      if (val === 0) {
        codeControl?.enable({ emitEvent: false });
      } else {
        codeControl?.disable({ emitEvent: false });
        codeControl?.setValue(null, { emitEvent: false }); 
      }
    });
  }

  determineMinAvailableId(): number {
    const existingIds = new Set((this.vats || []).map(v => v.id));

    let candidate = 1;
    while (existingIds.has(candidate)) {
      candidate++;
    }

    return candidate;
  }
  
  /**
   * Create buttons for the toolbar
   */
  createToolbarButtons = () => {
    this.toolbarEventsContextService.backButton$.next({
        isIt: signal(true),
        name: 'arrow_back',
        icon: signal('arrow_back'),
        click: () => this.back()
    });

    this.toolbarEventsContextService.buttons$.next({
        barButtons: [
            {
                isIt: () => true,
                name: 'save',
                icon: signal('check'),
                click: _ => {
                  this.save();
                }
            },
            {
                isIt: () => true,
                name: 'settings_backup_restore',
                icon: signal('settings_backup_restore'),
                click: _ => {
                  this.restore();
                }
            },
        ],
        panelButtons: []
    });

    this.toolbarEventsService.buttons$.next({
      barButtons: [
        {
          isIt: () => this.connection.isOffline(),
          name: 'cloud_off',
          icon: signal('cloud_off'),
          click: _ => null
        }
      ],
      panelButtons: [
        {
          isIt: signal(this.hasToolbarMenu),
          name: 'show_json_vat_rate',
          icon: signal('data_object'),
          label: signal('ITEMS.VAT_RATE.ACTIONS.SHOW_JSON'),
          click: _ => this.openVatRateJsonDetail()
        }
      ]
    })
  }
  
  async openVatRateJsonDetail() {
    await this.genericJsonDetailDialog.openDialog(
      { data: 
        { title:'ITEMS.VAT_RATE.ACTIONS.SHOW_JSON', 
        nameFile: 'item_json_data', 
        json: this.vatRateFormService.getVatRateFields(this.vatRate) } 
    });
  }
  
  back() {
    this.state.go(`app.new.items.vat-rate-management`);
    this.toolbarEventsContextService.buttons$.next({panelButtons: [], barButtons: []});
  }

  async restore() {
    let confirm = await this.confirmDialogService.openDialog({
      data: {
        messageLabel: 'ITEMS.VAT_RATE.DETAILS.UNDO_CHANGES',
      }
    });

    if(!confirm) return;
    
    this.vatRateFormService.resetFormValues(this.vatRate);
  }

  async save() {
    if(this.areEditsBlocked) {
      this.alertDialogService.openDialog({
        data: {
          messageLabel: this.translate.instant('ITEMS.NO_EDIT_WITH_MAGO_ENABLED')
        }})
      return;
    }

    if(this.vatRateFormService.form.invalid) {
      this.vatRateFormService.form.markAllAsTouched();
      this.alertDialogService.openDialog({
        data: {
          messageLabel: this.translate.instant('FORM_ERRORS.THERE_ARE_ERRORS')
        }})
      return;
    }

    let confirm = await this.confirmDialogService.openDialog({
      data: {
        messageLabel: 'ITEMS.VAT_RATE.DETAILS.WANT_TO_SAVE',
      }
    });

    if(!confirm) return;

    this.loaderService.show();

    const vatRateToSend = {
      ...this.vatRateFormService.getVatRateFields(this.vatRate)
    } as Vat;

    try {
      if(vatRateToSend.id != null) {
        this.vatRate = await this.entityManager.vat.putOneOnline(vatRateToSend);
      } else {
        vatRateToSend.id = this.determineMinAvailableId();
        this.vatRate = await this.entityManager.vat.postOneOnline(vatRateToSend);
      }

      // Aggiornamento lista aliquote IVA
      // const currentVats = this.itemDataCatalogService.getDataByKey('vats') || [];

      // if (vatRateToSend.id) {
      //   const index = currentVats.findIndex(v => v.id === this.vatRate.id);
      //   if (index !== -1) {
      //     currentVats[index] = this.vatRate;
      //   }
      // } else {
      //   currentVats.push(this.vatRate);
      // }

      // this.itemDataCatalogService.addDataToCatalog('vats', currentVats);

      this.state.go(`app.new.items.vat-rate-management`);

      this._snackBar.open(this.translate.instant('ITEMS.VAT_RATE.SAVED'), undefined, { 
        duration: 3000, 
        horizontalPosition: 'start', 
        verticalPosition: "bottom", 
      });
    } catch (e) {
      this.alertDialogService.openDialog({
        data: {
          messageLabel: vatRateToSend.id != null ? 'ITEMS.VAT_RATE.ERROR_IN_EDIT' : 'ITEMS.VAT_RATE.ERROR_IN_CREATION'
        }
      });
    } finally {
      this.loaderService.hide();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.vatRateFormReady.set(false);
  }
}
